'use client'

import * as React from 'react'

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { CircleIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

const RadioGroup: React.FC<
  React.ComponentProps<typeof RadioGroupPrimitive.Root>
> = ({ className, ...props }) => {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={twMerge('grid gap-4', className)}
      {...props}
    />
  )
}

const RadioGroupItem: React.FC<
  React.ComponentProps<typeof RadioGroupPrimitive.Item>
> = ({ className, ...props }) => {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={twMerge(
        // Base styling
        'relative aspect-square size-4 shrink-0 rounded-full transition-colors duration-100 outline-none',
        // Border and background
        'border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-800',
        // Hover states
        'hover:border-accent-300 hover:shadow-sm',
        'dark:hover:border-accent-600',
        // Focus states
        'focus-visible:ring-3 focus-visible:ring-accent-400/20 focus-visible:border-accent-400',
        'dark:focus-visible:ring-accent-400/30 dark:focus-visible:border-accent-400',
        // Checked states
        'data-[state=checked]:border-accent-400 data-[state=checked]:bg-accent-400',
        'dark:data-[state=checked]:bg-accent-400 dark:data-[state=checked]:border-accent-400',
        // Disabled states
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Invalid states
        'aria-invalid:border-red-400 aria-invalid:ring-2 aria-invalid:ring-red-400/20',
        'dark:aria-invalid:border-red-400 dark:aria-invalid:ring-red-400/30',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="absolute inset-0 flex items-center justify-center"
      >
        <CircleIcon className="size-2 fill-white text-white transition-colors duration-100" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
}

/**
 * Unstyled radio item for selectable-card rows: the whole card is the radio
 * control, so Radix supplies role/aria-checked/data-state and roving-tabindex
 * arrow-key navigation while the caller owns the visuals.
 */
const RadioGroupCardItem: React.FC<
  React.ComponentProps<typeof RadioGroupPrimitive.Item>
> = (props) => {
  return (
    <RadioGroupPrimitive.Item data-slot="radio-group-card-item" {...props} />
  )
}

export { RadioGroup, RadioGroupCardItem, RadioGroupItem }
